import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  emptyManualForm,
  type CampaignBuilderMode,
  type CampaignMediaItem,
  type CreativePreview,
  type PreviewData,
  type SelectedScreenMeta,
  type StepThreeData,
  type StepTwoData,
} from '@/features/campaigns/types/campaign';
import { isBillboardCreateLaunch } from '@/features/campaigns/types/billboardCreateLaunch';
import {
  MAX_SCREEN_SELECTION,
  MAX_CAMPAIGN_AMOUNT,
  MIN_CAMPAIGN_AMOUNT,
} from '@/features/campaigns/constants/campaignOptions';
import { formatMoney } from '@/shared/lib/format/money';
import { ApiError, getErrorMessage } from '@/shared/api/errors';
import {
  useCampaignStep1,
  useCampaignStep2,
  useCampaignStep3,
  useCampaignStep4,
  useCampaignReprice,
} from '@/features/campaigns/api/useCampaignMutations';
import { useAvailableCouponsQuery } from '@/features/campaigns/api/useCouponQueries';
import type { AppliedPreviewCoupon } from '@/features/campaigns/components/PreviewStep/PreviewStep.types';
import { clearStandardCampaignDraft } from '@/features/campaigns/utils/standardCampaignDraftStorage';
import { getCachedContentPreview } from '@/features/campaigns/utils/contentPreviewCache';
import { creativeSourcePayload, enumerateDates } from '@/features/campaigns/utils/campaignUtils';
import {
  getDatesMissingSlots,
  formatDateList,
  normalizeDateKey,
  getFullyBookedDates,
  findDayDataInSlots,
} from '@/features/campaigns/components/ScheduleStep/scheduleStepHelpers';
import { useManualAlert } from '@/features/campaigns/components/ManualBuilder/ManualAlert';
import { useAppSelector } from '@/shared/store/hooks';
import {
  isTeamMemberRole,
  requiresAgencyClient,
} from '@/shared/config/roles';
import { useInstantAdAccess } from '@/features/profile/hooks/useInstantAdAccess';
import { readInitialStandardState } from './campaignCreatePage.initialState';
import { useStandardDraftPersistence } from './useStandardDraftPersistence';
import { validateManualStepOne } from './validateManualStepOne';

const STEP_ONE_FIELD_LABELS: Record<string, string> = {
  agencyClientId: 'Client',
  campaignGoals: 'Campaign objective',
  campaignName: 'Campaign name',
  targetCities: 'Target cities',
  mediaId: 'Creative upload',
  customDuration: 'Ad duration',
  adType: 'Ad category',
  startDate: 'Start date',
  endDate: 'End date',
};

type UseStandardCampaignWizardParams = {
  routeMode?: string;
  builderMode: CampaignBuilderMode | null;
  isCampaignCompleted: boolean;
  showSuccess: boolean;
  availableBalance: number;
  onLaunched: (campaignId: string) => void;
  freshStart?: boolean;
  repetitionType?: string;
};

/** Owns the manual 4-step wizard: screen picks, schedule, pricing preview, and launch. */
export function useStandardCampaignWizard({
  routeMode,
  builderMode,
  isCampaignCompleted,
  showSuccess,
  availableBalance,
  onLaunched,
  freshStart = false,
  repetitionType,
}: UseStandardCampaignWizardParams) {
  const navigate = useNavigate();
  const location = useLocation();
  const alert = useManualAlert();
  const role = useAppSelector((s) => s.auth.user?.role);
  const needsClient = requiresAgencyClient(role);
  const isTeamMember = isTeamMemberRole(role);

  // Capture router launch flags once on mount (state is cleared on full refresh).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const { billboardLaunch, initial } = useMemo(() => {
    const launch = isBillboardCreateLaunch(location.state) ? location.state : null;
    return {
      billboardLaunch: launch,
      initial: readInitialStandardState(routeMode, launch, {
        fresh: Boolean(freshStart && !launch),
      }),
    };
  }, []);
  const skipScreenStep = Boolean(
    billboardLaunch?.billboardId || initial.skipScreenStep,
  );
  const instantAdAccess = useInstantAdAccess();

  const [currentStep, setCurrentStep] = useState(() => {
    if (freshStart) return 1;
    // If preview payload was dropped to fit storage, land on slots instead of a broken review page.
    if (
      initial.currentStep === 4 &&
      !initial.previewData?.pricing &&
      initial.submitData?.campaignId
    ) {
      return 3;
    }
    return initial.currentStep;
  });
  /** Highest step the user has reached — progress rail can jump back/forward within this. */
  const [maxStepReached, setMaxStepReached] = useState(() => {
    if (freshStart) return 1;
    if (
      initial.currentStep === 4 &&
      !initial.previewData?.pricing &&
      initial.submitData?.campaignId
    ) {
      return 3;
    }
    return initial.currentStep || 1;
  });

  useEffect(() => {
    setMaxStepReached((prev) => Math.max(prev, currentStep));
  }, [currentStep]);
  const [campaignData, setCampaignData] = useState(() =>
    freshStart ? emptyManualForm() : initial.campaignData,
  );
  const [stepTwoData, setStepTwoData] = useState<StepTwoData>(() =>
    freshStart
      ? { billboardIds: [], campaignId: '' }
      : initial.stepTwoData,
  );
  const [selectedScreensMeta, setSelectedScreensMeta] = useState<
    Record<string, SelectedScreenMeta>
  >(() => (freshStart ? {} : initial.selectedScreensMeta));
  const [submitData, setSubmitData] = useState<StepThreeData>(() =>
    freshStart
      ? { bookingSlots: [], campaignId: '' }
      : initial.submitData,
  );
  const [previewData, setPreviewData] = useState<PreviewData>(() =>
    freshStart ? {} : initial.previewData,
  );
  const [creativePreview, setCreativePreview] = useState<CreativePreview>(() =>
    freshStart ? { url: '', type: '' } : initial.creativePreview,
  );
  const [altMediaIds, setAltMediaIds] = useState<CampaignMediaItem[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [depositOpen, setDepositOpen] = useState(false);
  /** Bumps when slot hold expires so ScheduleStep remounts with fresh availability. */
  const [slotsSessionKey, setSlotsSessionKey] = useState(0);
  const [slotsHoldExpired, setSlotsHoldExpired] = useState(false);
  /** Tracks whether the latest screen pick was user-driven (manual) vs assist. */
  const [screenSelectionSource, setScreenSelectionSource] = useState<
    'auto' | 'manual' | null
  >(null);
  const [detectingOrientation, setDetectingOrientation] = useState(false);

  // Restore blob preview from in-memory cache after draft stripped blob URLs.
  useEffect(() => {
    const mediaId = campaignData.mediaIds?.[0]?.mediaId;
    const cached = getCachedContentPreview(mediaId);
    if (!cached?.blobUrl) return;
    if (creativePreview.url === cached.blobUrl) return;
    setCreativePreview({
      url: cached.blobUrl,
      type: cached.type || campaignData.mediaType || '',
    });
  }, [campaignData.mediaIds, campaignData.mediaType, creativePreview.url]);

  const step1 = useCampaignStep1({ silent: true });
  const step2 = useCampaignStep2({ silent: true });
  const step3 = useCampaignStep3({ silent: true });
  const step4 = useCampaignStep4({ silent: true });
  const reprice = useCampaignReprice();

  const campaignCost = Number(previewData?.pricing?.amountWithTax || 0);
  const couponDiscount = Number(
    previewData?.coupon?.discountAmount ??
    previewData?.pricing?.discountAmount ??
    previewData?.pricing?.totalDiscountAmount ??
    previewData?.summary?.discountAmount ??
    0,
  );
  const payable = Number(
    previewData?.pricing?.totalAmount ||
    Math.max(0, campaignCost - couponDiscount),
  );
  // Offers are listed against the pre-discount total so the applied one stays visible.
  const { coupons, isLoading: isCouponsLoading } = useAvailableCouponsQuery(
    campaignCost + couponDiscount,
    currentStep === 4,
  );

  /** Sends couponCode on step 3 so the backend re-prices the existing booking. */
  const repriceWithCoupon = (code: string) => {
    if (!submitData.campaignId || reprice.isPending) return;
    const payload: Record<string, unknown> = {
      campaignId: submitData.campaignId,
      bookingSlots: submitData.bookingSlots,
      couponCode: code,
    };
    if (submitData.appliedToAll) payload.appliedToAll = true;

    const applyPreview = (nextPreview: PreviewData) => {
      setPreviewData((prev) => {
        const localSlots = submitData.bookingSlots?.length ?? 0;
        const prevSlots = Number(prev?.analytics?.totalSlots);
        const nextSlots = Number(nextPreview?.analytics?.totalSlots);
        const bookedLen = Array.isArray(nextPreview.bookedSlots)
          ? nextPreview.bookedSlots.length
          : Array.isArray(prev?.bookedSlots)
            ? prev.bookedSlots.length
            : localSlots;
        const totalSlots =
          (Number.isFinite(nextSlots) && nextSlots > 0
            ? nextSlots
            : Number.isFinite(prevSlots) && prevSlots > 0
              ? prevSlots
              : bookedLen) || localSlots;
        return {
          ...nextPreview,
          analytics: {
            ...(prev?.analytics || {}),
            ...(nextPreview.analytics || {}),
            totalSlots:
              nextPreview?.analytics?.totalSlots ?? totalSlots,
          },
          bookedSlots:
            nextPreview.bookedSlots ||
            prev?.bookedSlots ||
            submitData.bookingSlots ||
            [],
        };
      });
      const responseCode = nextPreview.coupon?.couponCode?.trim() || '';
      setSubmitData((prev) => ({
        ...prev,
        couponCode: code ? responseCode || code : '',
      }));
    };

    reprice.mutate(payload, {
      onSuccess: (data) => {
        const nextPreview = data?.data;
        if (!nextPreview) {
          alert.error(
            'Coupon not applied',
            data?.message || 'Could not update pricing with this coupon.',
            { actions: [{ label: 'OK', primary: true }] },
          );
          return;
        }
        applyPreview(nextPreview);
        alert.success('Coupon applied', 'Pricing has been updated.');
      },
      onError: (error) => {
        const details =
          error instanceof ApiError && error.details && typeof error.details === 'object'
            ? (error.details as { data?: PreviewData }).data
            : undefined;
        if (details && (details.pricing || details.coupon || details.summary)) {
          applyPreview(details);
          return;
        }
        alert.error(
          'Coupon not applied',
          getErrorMessage(error, 'Could not update pricing with this coupon.'),
        );
      },
    });
  };

  const appliedCoupons: AppliedPreviewCoupon[] = [];
  const previewCode = previewData.coupon?.couponCode?.trim();
  const userCode = submitData.couponCode?.trim();
  if (previewCode) {
    appliedCoupons.push({
      code: previewCode,
      discount: Number(previewData.coupon?.discountAmount || couponDiscount),
      autoApplied: Boolean(previewData.coupon?.autoApplied),
    });
  } else if (userCode) {
    appliedCoupons.push({
      code: userCode,
      discount: couponDiscount,
      autoApplied: false,
    });
  }

  const coupon = {
    applied: appliedCoupons,
    coupons,
    isLoading: isCouponsLoading,
    isBusy: reprice.isPending,
    onApply: (code: string) => {
      const trimmed = code.trim();
      if (trimmed) repriceWithCoupon(trimmed);
    },
    onRemove: (code: string) => {
      if (code) repriceWithCoupon('');
    },
  };

  const belowMinOrder = campaignCost < MIN_CAMPAIGN_AMOUNT;
  const exceedsMaxOrder = campaignCost > MAX_CAMPAIGN_AMOUNT;
  const launchShortfall = Math.max(0, payable - availableBalance);
  const needsTopUp =
    currentStep === 4 &&
    !belowMinOrder &&
    !exceedsMaxOrder &&
    launchShortfall > 0;
  /** Exact amount needed to cover this campaign (not forced to 1000). */
  const depositAmount = Math.ceil(launchShortfall * 100) / 100;

  useStandardDraftPersistence({
    builderMode,
    isCampaignCompleted,
    showSuccess,
    draft: {
      currentStep,
      campaignData,
      stepTwoData,
      selectedScreensMeta,
      submitData,
      previewData,
      creativePreview,
      skipScreenStep,
    },
  });

  const clearError = useCallback((key: string) => {
    setErrors((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }, []);

  const resetManual = useCallback(() => {
    clearStandardCampaignDraft();
    setCurrentStep(1);
    setCampaignData(emptyManualForm());
    setStepTwoData({ billboardIds: [], campaignId: '' });
    setSelectedScreensMeta({});
    setSubmitData({ bookingSlots: [], campaignId: '' });
    setPreviewData({});
    setCreativePreview({ url: '', type: '' });
    setErrors({});
  }, []);

  /** Slot hold expired — keep campaign/screens/creative; only re-pick timeslots. */
  const releaseSlotHold = useCallback(() => {
    setDepositOpen(false);
    setSubmitData((prev) => ({
      ...prev,
      bookingSlots: [],
    }));
    setPreviewData({});
    setAltMediaIds([]);
    setSlotsHoldExpired(true);
    setSlotsSessionKey((k) => k + 1);
    setCurrentStep(3);
  }, []);

  /** Clear booked hours when switching Same-for-all ↔ Per-screen on Step 3. */
  const resetBookingSlots = useCallback(() => {
    setSubmitData((prev) => ({
      ...prev,
      bookingSlots: [],
    }));
    setPreviewData({});
    setAltMediaIds([]);
    setSlotsHoldExpired(false);
    setSlotsSessionKey((k) => k + 1);
    setErrors((prev) => {
      if (!prev.screenSlots) return prev;
      const next = { ...prev };
      delete next.screenSlots;
      return next;
    });
  }, []);

  const handleSelectScreen = (
    screen: Record<string, unknown>,
    opts?: { source?: 'auto' | 'manual' },
  ) => {
    if (skipScreenStep) return;
    const screenId = String(screen._id || screen.id || '');
    if (!screenId) return;
    const isSelected = stepTwoData.billboardIds.includes(screenId);
    const source = opts?.source ?? 'manual';

    // Reset slot repetitions & bookings whenever screens selection changes
    setSubmitData((prev) => ({
      ...prev,
      bookingSlots: [],
    }));
    setPreviewData({});

    if (!isSelected && stepTwoData.billboardIds.length >= MAX_SCREEN_SELECTION) {
      alert.warning(
        'Screen limit reached',
        `You can select up to ${MAX_SCREEN_SELECTION} screens. Deselect one to add another.`,
        { actions: [{ label: 'Got it', primary: true }] },
      );
      return;
    }

    if (isSelected) {
      setStepTwoData((prev) => ({
        ...prev,
        billboardIds: prev.billboardIds.filter((id) => id !== screenId),
      }));
      setSelectedScreensMeta((prev) => {
        const next = { ...prev };
        delete next[screenId];
        return next;
      });
      setScreenSelectionSource(null);
      return;
    }

    const address = screen.address as
      | { building?: string; city?: string; state?: string }
      | undefined;
    const thumbnail = screen.thumbnail as { url?: string } | undefined;

    setStepTwoData((prev) => ({
      ...prev,
      billboardIds: [...prev.billboardIds, screenId],
    }));
    setSelectedScreensMeta((prev) => ({
      ...prev,
      [screenId]: {
        id: screenId,
        screenName: String(screen.screenName || screen.name || 'Screen'),
        thumbnail: thumbnail?.url || '',
        addressLine: [address?.building, address?.city, address?.state]
          .filter(Boolean)
          .join(' / '),
        city: address?.city || 'Unknown',
        screenOrientation: String(
          screen.screenOrientation || screen.orientation || '',
        ).toUpperCase() || undefined,
      },
    }));
    setScreenSelectionSource(source);
    clearError('billboardIds');
  };

  const handleClearAllScreens = () => {
    setSelectedScreensMeta({});
    setStepTwoData((prev) => ({ ...prev, billboardIds: [] }));
    setScreenSelectionSource(null);
    setSubmitData((prev) => ({ ...prev, bookingSlots: [] }));
    setPreviewData({});
  };

  const handleBatchSelectScreens = (
    screensToSelect: Record<string, unknown>[],
    action: 'select' | 'deselect' = 'select',
  ) => {
    setSubmitData((prev) => ({
      ...prev,
      bookingSlots: [],
    }));
    setPreviewData({});

    if (action === 'deselect') {
      const idsToRemove = new Set(
        screensToSelect.map((s) => String(s._id || s.id || '')).filter(Boolean),
      );
      setStepTwoData((prev) => ({
        ...prev,
        billboardIds: prev.billboardIds.filter((id) => !idsToRemove.has(id)),
      }));
      setSelectedScreensMeta((prev) => {
        const next = { ...prev };
        idsToRemove.forEach((id) => delete next[id]);
        return next;
      });
      return;
    }

    const currentIds = new Set(stepTwoData.billboardIds);
    const newItems: Record<string, SelectedScreenMeta> = {};
    const newIds: string[] = [];

    for (const screen of screensToSelect) {
      const id = String(screen._id || screen.id || '');
      if (!id || currentIds.has(id)) continue;
      if (currentIds.size + newIds.length >= MAX_SCREEN_SELECTION) {
        alert.info(
          'Selection limited',
          `Only ${MAX_SCREEN_SELECTION} screens can be selected at once.`,
        );
        break;
      }
      newIds.push(id);
      const address = screen.address as
        | { building?: string; city?: string; state?: string }
        | undefined;
      const thumbnail = screen.thumbnail as { url?: string } | undefined;
      newItems[id] = {
        id,
        screenName: String(screen.screenName || screen.name || 'Screen'),
        thumbnail: thumbnail?.url || '',
        addressLine: [address?.building, address?.city, address?.state]
          .filter(Boolean)
          .join(' / '),
        city: address?.city || 'Unknown',
        screenOrientation: String(
          screen.screenOrientation || screen.orientation || '',
        ).toUpperCase() || undefined,
      };
    }

    if (newIds.length > 0) {
      setStepTwoData((prev) => ({
        ...prev,
        billboardIds: [...prev.billboardIds, ...newIds],
      }));
      setSelectedScreensMeta((prev) => ({
        ...prev,
        ...newItems,
      }));
      setScreenSelectionSource('manual');
      clearError('billboardIds');
      alert.success(
        'Screens selected',
        `Added ${newIds.length} display${newIds.length > 1 ? 's' : ''} to your campaign.`,
        { actions: [{ label: 'Continue', primary: true }] },
      );
    }
  };

  const goToScheduleAfterStep2 = (
    campaignId: string,
    billboardIds: string[],
  ) => {
    step2.mutate(
      { billboardIds, campaignId },
      {
        onSuccess: (data) => {
          const nextId = data?.data?.campaignId || campaignId;
          setSubmitData((prev) => ({ ...prev, campaignId: nextId }));
          setStepTwoData((prev) => ({ ...prev, campaignId: nextId }));
          setCurrentStep(3);
        },
        onError: (error) => {
          alert.error(
            'Couldn’t save screens',
            getErrorMessage(error, 'Failed to save selected screens. Try again.'),
            { actions: [{ label: 'Try again', primary: true }] },
          );
        },
      },
    );
  };

  const handleNext = () => {
    if (currentStep === 1) {
      const validationErrors = validateManualStepOne(campaignData, {
        requireAgencyClient: needsClient,
        allowToday: skipScreenStep,
        instantAdAccess,
      });
      setErrors(validationErrors);
      if (Object.keys(validationErrors).length > 0) {
        const items = Object.entries(validationErrors).map(
          ([key, message]) =>
            `${STEP_ONE_FIELD_LABELS[key] || key}: ${message}`,
        );
        alert.error(
          'Complete required details',
          'Fix the items below, then continue.',
          {
            items,
            actions: [{ label: 'Review form', primary: true }],
          },
        );
        return;
      }
      const { mediaUrl: _u, agencyClientId, galleryId, mediaIds, ...rest } =
        campaignData;
      const payload = {
        ...rest,
        ...creativeSourcePayload({ galleryId, mediaIds }),
        ...(agencyClientId ? { agencyClientId } : {}),
      };
      step1.mutate(payload as unknown as Record<string, unknown>, {
        onSuccess: (data) => {
          const campaignId = data?.data?.campaignId || '';
          const billboardIds = skipScreenStep
            ? stepTwoData.billboardIds.length
              ? stepTwoData.billboardIds
              : billboardLaunch?.billboardId
                ? [billboardLaunch.billboardId]
                : []
            : stepTwoData.billboardIds;

          setStepTwoData((prev) => ({
            ...prev,
            campaignId,
            ...(skipScreenStep ? { billboardIds } : {}),
          }));
          setSubmitData((prev) => ({ ...prev, campaignId }));

          if (skipScreenStep) {
            if (!billboardIds.length) {
              alert.error(
                'Screen missing',
                'Please start again from the billboard page.',
                {
                  actions: [
                    {
                      label: 'Back to screens',
                      primary: true,
                      onClick: () => navigate('/screens'),
                    },
                  ],
                },
              );
              return;
            }
            goToScheduleAfterStep2(campaignId, billboardIds);
            return;
          }
          setCurrentStep(2);
        },
        onError: (error) => {
          alert.error(
            'Couldn’t save campaign basics',
            getErrorMessage(error, 'Failed to create campaign draft. Try again.'),
          );
        },
      });
      return;
    }

    if (currentStep === 2) {
      if (!stepTwoData.billboardIds.length) {
        setErrors({ billboardIds: 'Select at least one screen' });
        alert.warning(
          'No screens selected',
          'Choose at least one display to continue to scheduling.',
          {
            actions: [
              { label: 'Select screens', primary: true },
            ],
          },
        );
        return;
      }
      goToScheduleAfterStep2(stepTwoData.campaignId, stepTwoData.billboardIds);
      return;
    }

    if (currentStep === 3) {
      if (!submitData.bookingSlots.length) {
        alert.warning(
          'No time slots booked',
          'Tap hours on the schedule to book plays, then continue.',
          { actions: [{ label: 'Book hours', primary: true }] },
        );
        return;
      }

      // In screen-wise schedule mode, every selected screen MUST have at least one time slot (0 slots is not allowed)
      if (repetitionType === 'screen_wise' && stepTwoData.billboardIds.length > 0) {
        const bookedBillboardIds = new Set(
          submitData.bookingSlots.map((s) => s.billboardId).filter(Boolean),
        );
        const screensWithoutSlots = stepTwoData.billboardIds.filter(
          (id) => !bookedBillboardIds.has(id),
        );

        if (screensWithoutSlots.length > 0) {
          // ScreenWiseScheduleStep opens an actionable “Book next display” alert.
          const count = screensWithoutSlots.length;
          setErrors((prev) => ({
            ...prev,
            screenSlots: `${count} display${count === 1 ? '' : 's'} still need hours`,
          }));
          return;
        }
        setErrors((prev) => {
          const next = { ...prev };
          delete next.screenSlots;
          return next;
        });
      }

      if (!submitData.appliedToAll) {
        const allDates = enumerateDates(
          campaignData.startDate,
          campaignData.endDate,
          366,
        );
        const slotsData = submitData.slotsData || {};
        const bookedHours = new Set(submitData.bookingSlots.map((s) => s.hour));

        const fullyBooked = getFullyBookedDates(allDates, slotsData);
        const fullyBookedSet = new Set(fullyBooked.map(normalizeDateKey));

        // Also exclude dates where none of the user's chosen hours are available
        for (const d of allDates) {
          const normD = normalizeDateKey(d);
          if (!fullyBookedSet.has(normD) && bookedHours.size > 0) {
            const dayData = findDayDataInSlots(d, slotsData);
            if (dayData && Array.isArray(dayData.availableSlots)) {
              const hasBookedHourAvail = dayData.availableSlots.some(
                (h) => bookedHours.has(h.hour) && Number(h.availableRepetitions ?? 0) > 0,
              );
              if (!hasBookedHourAvail) {
                fullyBookedSet.add(normD);
              }
            }
          }
        }

        const missingDates = getDatesMissingSlots(
          submitData.bookingSlots,
          allDates,
          undefined,
          Array.from(fullyBookedSet),
        );

        if (missingDates.length > 0) {
          alert.warning(
            'Dates still need hours',
            `Book slots for every available date, or turn on sync across dates.`,
            {
              items: [
                `${missingDates.length} date${missingDates.length === 1 ? '' : 's'} remaining: ${formatDateList(missingDates)}`,
              ],
              actions: [{ label: 'Continue booking', primary: true }],
            },
          );
          return;
        }
      }

      step3.mutate(submitData as unknown as Record<string, unknown>, {
        onSuccess: (data) => {
          const nextPreview = data?.data || {};
          // Ensure analytics.totalSlots is present for Step 4 overview when API omits it
          const apiSlots = Number(nextPreview?.analytics?.totalSlots);
          const localSlots = submitData.bookingSlots?.length ?? 0;
          const bookedFromApi = Array.isArray(nextPreview?.bookedSlots)
            ? nextPreview.bookedSlots.length
            : 0;
          const resolvedSlots =
            (Number.isFinite(apiSlots) && apiSlots > 0
              ? apiSlots
              : bookedFromApi > 0
                ? bookedFromApi
                : localSlots) || 0;
          setPreviewData({
            ...nextPreview,
            analytics: {
              ...(nextPreview.analytics || {}),
              totalSlots:
                nextPreview?.analytics?.totalSlots ?? resolvedSlots,
            },
            bookedSlots:
              nextPreview.bookedSlots ||
              submitData.bookingSlots ||
              [],
          });
          setSlotsHoldExpired(false);
          setCurrentStep(4);
          const total = Number(nextPreview?.pricing?.amountWithTax || 0);
          if (total > MAX_CAMPAIGN_AMOUNT) {
            alert.error(
              'Order exceeds maximum',
              `Total is above ${formatMoney(MAX_CAMPAIGN_AMOUNT)}. Reduce screens, dates, or slots to continue.`,
              {
                actions: [
                  {
                    label: 'Back to slots',
                    primary: true,
                    onClick: () => setCurrentStep(3),
                  },
                ],
              },
            );
          }
        },
        onError: (error) => {
          alert.error(
            'Couldn’t book slots',
            getErrorMessage(error, 'Failed to save schedule. Try again.'),
          );
        },
      });
      return;
    }

    if (belowMinOrder) {
      alert.warning(
        'Order below minimum',
        `Minimum order is ${formatMoney(MIN_CAMPAIGN_AMOUNT)}. Go back and book more slots.`,
        {
          actions: [
            {
              label: 'Back to slots',
              primary: true,
              onClick: () => setCurrentStep(3),
            },
          ],
        },
      );
      return;
    }

    if (exceedsMaxOrder) {
      alert.error(
        'Order exceeds maximum',
        `Maximum campaign order is ${formatMoney(MAX_CAMPAIGN_AMOUNT)}. Reduce screens, dates, or slots.`,
        {
          actions: [
            {
              label: 'Back to slots',
              primary: true,
              onClick: () => setCurrentStep(3),
            },
          ],
        },
      );
      return;
    }

    if (launchShortfall > 0) {
      if (isTeamMember) {
        alert.error(
          'Insufficient wallet balance',
          'Ask your agency admin to add funds before launching.',
          { actions: [{ label: 'OK', primary: true }] },
        );
        return;
      }
      setDepositOpen(true);
      return;
    }

    step4.mutate(
      {
        campaignId: submitData.campaignId,
        ...(altMediaIds.length
          ? {
            mediaIds: altMediaIds.map(
              ({ mediaId, orientation, mediaType }) => ({
                mediaId,
                orientation,
                mediaType,
              }),
            ),
          }
          : {}),
      },
      {
        onSuccess: (data) => {
          clearStandardCampaignDraft();
          onLaunched(data?.data?.campaignId || submitData.campaignId);
        },
        onError: (error) => {
          alert.error(
            'Launch failed',
            getErrorMessage(error, 'Could not launch campaign. Try again.'),
          );
        },
      },
    );
  };

  const handleDepositSuccess = () => {
    setDepositOpen(false);
    alert.success(
      'Payment successful',
      'Launching your campaign now…',
      { actions: [{ label: 'OK', primary: true }] },
    );
    step4.mutate(
      {
        campaignId: submitData.campaignId,
        ...(altMediaIds.length
          ? {
            mediaIds: altMediaIds.map(
              ({ mediaId, orientation, mediaType }) => ({
                mediaId,
                orientation,
                mediaType,
              }),
            ),
          }
          : {}),
      },
      {
        onSuccess: (data) => {
          clearStandardCampaignDraft();
          onLaunched(data?.data?.campaignId || submitData.campaignId);
        },
        onError: (error) => {
          alert.error(
            'Launch failed',
            getErrorMessage(error, 'Payment succeeded but launch failed. Try launching again.'),
          );
        },
      },
    );
  };

  const handleBack = () => {
    if (currentStep <= 1) {
      clearStandardCampaignDraft();
      navigate(
        skipScreenStep && billboardLaunch?.billboardId
          ? `/billboard/${billboardLaunch.billboardId}`
          : '/campaigns',
      );
      return;
    }
    if (skipScreenStep && currentStep === 3) {
      setCurrentStep(1);
      return;
    }
    setCurrentStep((s) => s - 1);
  };

  const isBusy = useMemo(
    () =>
      step1.isPending ||
      step2.isPending ||
      step3.isPending ||
      step4.isPending,
    [step1.isPending, step2.isPending, step3.isPending, step4.isPending],
  );

  /** Progress-rail navigation: revisit any reached step; cannot skip ahead. */
  const goToStep = useCallback(
    (stepId: number) => {
      if (isBusy) return;
      if (stepId === currentStep) return;
      if (skipScreenStep && stepId === 2) return;
      if (stepId < 1 || stepId > 4) return;

      if (stepId < currentStep) {
        setCurrentStep(stepId);
        return;
      }

      if (stepId <= maxStepReached) {
        setCurrentStep(stepId);
        return;
      }

      alert.info(
        'Finish this step first',
        'Use Continue to move forward. You can jump back to earlier steps anytime from the progress bar.',
        { actions: [{ label: 'Got it', primary: true }] },
      );
    },
    [alert, currentStep, isBusy, maxStepReached, skipScreenStep],
  );

  return {
    currentStep,
    campaignData,
    setCampaignData,
    stepTwoData,
    selectedScreensMeta,
    submitData,
    setSubmitData,
    previewData,
    creativePreview,
    setCreativePreview,
    altMediaIds,
    setAltMediaIds,
    errors,
    clearError,
    isBusy,
    coupon,
    needsTopUp,
    belowMinOrder,
    exceedsMaxOrder,
    launchShortfall,
    depositOpen,
    depositAmount,
    closeDeposit: () => setDepositOpen(false),
    handleDepositSuccess,
    skipScreenStep,
    handleSelectScreen,
    handleBatchSelectScreens,
    handleClearAllScreens,
    handleNext,
    handleBack,
    goToStep,
    maxStepReached,
    resetManual,
    releaseSlotHold,
    resetBookingSlots,
    slotsSessionKey,
    slotsHoldExpired,
    screenSelectionSource,
    setScreenSelectionSource,
    detectingOrientation,
    setDetectingOrientation,
  };
}
